<template>
  <f7-link
    class="chat-feed-card"
    @click="$emit('openFeedItem', feedCard)"
    role="button"
    tabindex="0"
    @keydown.enter="$emit('openFeedItem', feedCard)"
    @keydown.space.prevent="$emit('openFeedItem', feedCard)"
  >
    <div class="cfc-type-badge">
      <f7-icon :f7="feedCard.type === 'POLL' || feedCard.type === 'SPONSORED_POLL' ? 'chart_bar' : 'doc_text'" :size="10" />
      <span>{{ feedCard.type === 'POLL' || feedCard.type === 'SPONSORED_POLL' ? $t('tamtammessage.poll') : $t('tamtammessage.post') }}</span>
    </div>
    <div class="cfc-header">
      <div class="cfc-avatar" :style="avatarStyle">{{ initials }}</div>
      <div class="cfc-meta">
        <div class="cfc-author">{{ feedCard.authorName }}</div>
        <div class="cfc-sub" v-if="feedCard.groupName">{{ feedCard.groupName }}</div>
      </div>
    </div>
    <div v-if="feedCard.headline" class="cfc-headline">{{ feedCard.headline }}</div>
    <div v-if="feedCard.text" class="cfc-text">{{ truncatedText }}</div>
    <div v-if="feedCard.media && feedCard.media.length > 0" class="cfc-media">
      <img :src="feedCard.media[0]" class="cfc-media-img" />
      <span v-if="feedCard.media.length > 1" class="cfc-media-count">+{{ feedCard.media.length - 1 }}</span>
    </div>
    <div v-if="feedCard.poll" class="cfc-poll">
      <div v-for="(opt, idx) in feedCard.poll.options" :key="idx" class="cfc-poll-opt">
        {{ opt.label }}
      </div>
    </div>
    <div class="cfc-footer">
      <f7-icon f7="link" :size="10" />
      <span>{{ $t('tamtammessage.viewOnFeed') }}</span>
    </div>
  </f7-link>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  feedCard: { type: Object, required: true },
})

defineEmits(['openFeedItem'])

const textLimit = 120

const truncatedText = computed(() => {
  const text = props.feedCard.text || ''
  if (text.length <= textLimit) return text
  return text.substring(0, text.lastIndexOf(' ', textLimit)) + '…'
})

const initials = computed(() => {
  const name = props.feedCard.authorName || ''
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = (props.feedCard.authorName || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  const bg = colors[hash % colors.length]
  return { background: bg, color: '#fff' }
})
</script>

<style scoped>
.chat-feed-card {
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 8px;
  overflow: hidden;
  background: var(--f7-page-bg-color, #fff);
  max-width: 280px;
  font-size: 11px;
  text-decoration: none;
  color: inherit;
  display: block;
}
.cfc-type-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--f7-list-bg-color, #f5f5f5);
  font-size: 9px;
  font-weight: 500;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.cfc-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px 2px;
}
.cfc-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 500;
  flex-shrink: 0;
}
.cfc-meta { flex: 1; min-width: 0; }
.cfc-author { font-size: 11px; font-weight: 500; }
.cfc-sub { font-size: 9px; color: var(--f7-on-surface-variant, #8e8e93); }
.cfc-headline {
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
}
.cfc-text {
  padding: 0 8px 4px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--f7-text-color, #000);
}
.cfc-media {
  position: relative;
  margin: 0 8px 4px;
}
.cfc-media-img {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 4px;
}
.cfc-media-count {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
}
.cfc-poll {
  padding: 0 8px 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cfc-poll-opt {
  font-size: 10px;
  padding: 3px 6px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 4px;
}
.cfc-footer {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  color: var(--f7-theme-color, #1A6B42);
  font-size: 9px;
  font-weight: 500;
  border-top: 0.5px solid var(--f7-list-item-border-color, #e0e0e0);
}
</style>
